import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import {
  TEDLRFakturaAttestAttestTypEnum,
  TEDLRFakturaAttestSTATUSEnum,
  TEDLRLeverantorfakturaMOMSHANTERINGEnum,
} from '../../../../../../../../../globalQueryTypes';
import { ModelContext } from '../../../../../../../../../Model/ModelContext/ModelContext';
import { LoginStateContext } from '../../../../../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import { BusinessDataFormContext } from '../../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  mergeBusinessDataFormValues,
  parseText,
} from '../../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import KonteringContext from '../../../../../../../_Common/Components/KonteringManager/KonteringContext';
import { IKodstrang } from '../../../../../../../_Common/Components/KonteringManager/KonteringManager';
import {
  filterKodbehOnKodstrang,
  IFilterKodbehOnKodstrangResult,
} from '../../../../../../../_Common/Utils/kodbehorighetUtils';
import { FakturaattestWrapperContext } from '../../../../../../Rutiner/FakturaDel/Contexts/FakturaattestWrapperContext';
import { LRSysOptionsQuery_db_SYSOptions_All } from '../../../../Queries/LRSysOptionQuery/_QueryTypes/LRSysOptionsQuery';
import { FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All } from '../../../../Queries/_QueryTypes/FakturaDelQuery';
import {
  getAktuellFakturaAttest,
  getKonteringRadtyp,
  isAutomaticallyAdded,
} from '../../../../Utils/FakturadelUtils';
import FakturaDelContext from '../../../FakturaDelContextManager/FakturaDelContext';
import {
  AUTOMATICALLY_ADDED_VALUE_PREFIX,
  konteringsforslagWithoutDeletedKontodelar,
} from '../../../FakturaDelContextManager/FakturaDelContextManager';
import KonteringForslagComp from '../../KonteringsForslagComp';
import {
  roundToTwoDecimals,
  konteringSaknas,
  getOrdinarieKodbehorigheter,
  getKodstrangar,
  getKonteratBelopp,
  getNewKonteringsforslagValues,
  getKonteringAvdragsRatt,
  getUtlandskAvdrag,
  getRubrikbelopp,
} from '../../Utils/KonteringUtils';
import EDList from '../../../../../../../../../BaseComponents/List/EDList';
import EDBox from '../../../../../../../../../BaseComponents/EDBox/EDBox';
import shortid from 'shortid';
import EDKeybind from '../../../../../../../../../BaseComponents/Keybinds/EDKeybind';
import { VirtuosoHandle } from 'react-virtuoso';
import { useIsBreakpointOrSmaller } from '../../../../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import { FakturaDelType } from '../../../../Queries/_QueryTypes/FakturaDelType';
import { FormUpdateAssociatonType } from '../../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';

const KONTERINGFORSLAG_PIXEL_HEIGHT = 85;

interface IProps {
  isFIActive: boolean;
}

const KonteringViewComp: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { avenyCollectionMap } = useContext(ModelContext);
  const konteringContext = useContext(KonteringContext);
  const formContext = useContext(BusinessDataFormContext);
  const loginStateContext = useContext(LoginStateContext);
  const fakturaDelContext = useContext(FakturaDelContext);
  const { setActiveKF } = fakturaDelContext;
  const fakturaattestContext = useContext(FakturaattestWrapperContext);
  const isSmallOrMedium = useIsBreakpointOrSmaller('md');

  const virtuoso = useRef<VirtuosoHandle>(null);

  const [newBeloppChangedId, setNewBeloppChangedId] = useState<string | null>(
    null
  );
  const [expandedKF, setExpandedKF] = useState<string[]>([]);

  const requireMotKodbeh: boolean = useMemo(() => {
    return (
      fakturaattestContext.sysOptions?.filter(
        (option: LRSysOptionsQuery_db_SYSOptions_All) =>
          option.OptionKey === 'LR_REQUIRE_MOTKODBEH'
      )?.[0]?.OptionData === 'Y'
    );
  }, [fakturaattestContext.sysOptions]);

  // När det sker en förändring i ett konteringsförslag så uppdaterar vi kodbehörigheten för just det konteringsförslaget
  useDeepCompareEffect(() => {
    const fakturaData = formContext?.data as FakturaDelType;
    const dataFormFakturaAttest = (fakturaData?._Associations as any)
      ?.Fakturaattest_All;

    const kodplanId =
      formContext?.data?._Associations?.Faktura?._Associations?.Registrering
        ?._Associations?.Bokfverifikationer?._Associations?.Kodplan?.id;

    const attestTyp: TEDLRFakturaAttestAttestTypEnum =
      formContext?.data?.ATTEST_STATUS === 'BESLUTSATTESTERAS'
        ? TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST
        : TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST;

    const minFakturaAttest = getAktuellFakturaAttest(
      dataFormFakturaAttest,
      fakturaattestContext.reservForData,
      loginStateContext.sign,
      attestTyp
    );

    const ordinarieAttestantKodbehorigheter = getOrdinarieKodbehorigheter(
      fakturaattestContext.reservForKodbehorigheter,
      minFakturaAttest,
      kodplanId,
      avenyCollectionMap,
      konteringContext.attestKodbehorigheterUrval,
      requireMotKodbeh
    );

    const mergedDataWithChanges = mergeBusinessDataFormValues(
      parseText(formContext?.data),
      parseText(formContext?.formState.values ?? {})
    );

    const updatedValues = formContext?.formState.values ?? {};

    const allaForslag: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All[] =
      mergedDataWithChanges._Associations?.Faktura?._Associations?.Konteringsforslag?.filter(
        konteringsforslagWithoutDeletedKontodelar
      ).filter((kf: any) => {
        // Hack för att få bort deletade konteringsförslag (ska rättas "på riktigt" i GR)
        return !updatedValues._Associations?.Faktura_Values?._Associations?.Konteringsforslag_Delete_Values?.some(
          (dkf: any) => dkf.id === kf.id
        );
      }) ?? [];

    const allKodstrangar: IKodstrang[] = allaForslag.map(getKodstrangar) ?? [];

    allKodstrangar.map(kodstrang => {
      const filtreradeKodbeh: IFilterKodbehOnKodstrangResult =
        filterKodbehOnKodstrang(ordinarieAttestantKodbehorigheter, kodstrang);

      konteringContext.konteringUtils.setKonteringsForslagWithKodbeh(
        kodstrang.id,
        filtreradeKodbeh.attestKodbehorighetUrval,
        filtreradeKodbeh.correctnessKodbehorighetUrval,
        fakturaData.id
      );
    });
    formContext?.formStateDispatch({ type: 'VALIDATE' });
  }, [
    fakturaDelContext.konteringsforslag,
    formContext?.data?.ATTEST_STATUS,
    formContext?.data?._Associations?.Fakturaattest_All,
  ]);

  const currentFakturaAttest = useMemo(
    () => fakturaDelContext.minFakturaAttest.aktuellFakturaAttest,
    [fakturaDelContext.minFakturaAttest.aktuellFakturaAttest]
  );

  const contextKonteringsforslag = useMemo(() => {
    return fakturaDelContext.konteringsforslag;
  }, [fakturaDelContext.konteringsforslag]);

  const numberOfSavedKonteringsforslag =
    formContext?.data?._Associations?.Faktura?._Associations
      ?.Konteringsforslag_All?.length ?? 0;

  const containerHeight = useMemo(
    () =>
      contextKonteringsforslag.length > 10
        ? KONTERINGFORSLAG_PIXEL_HEIGHT * 7.5 // 7.5 Konteringsförslag
        : KONTERINGFORSLAG_PIXEL_HEIGHT * contextKonteringsforslag.length,
    [contextKonteringsforslag]
  );
  const toggleExpandIndex = useCallback(
    (konteringsForslagId: string) => (_: React.SyntheticEvent<Element, Event>, newValue: boolean) => {
      if (newValue) {
        setExpandedKF([...expandedKF, konteringsForslagId]);
      } else {
        setExpandedKF(
          expandedKF?.filter(id => id != konteringsForslagId) ?? []
        );
      }
    },
    [expandedKF]
  );

  const handleCloseKF = useCallback(() => {
    setExpandedKF([fakturaDelContext.activeKF]);

    if (fakturaDelContext.activeKF) {
      if (numberOfSavedKonteringsforslag > 25) {
        const activeKFIndex = contextKonteringsforslag.findIndex(
          konteringsforslag =>
            konteringsforslag.id === fakturaDelContext.activeKF
        );
        virtuoso.current?.scrollToIndex({
          index: activeKFIndex === -1 ? 0 : activeKFIndex,
          align: 'center',
          behavior: 'smooth',
        });
      } else {
        const element = document.querySelector(
          `#${fakturaDelContext.activeKF}`
        );

        if (element) {
          //Väntar på att animationen för att stänga mui:s accordion.
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 300);
        }
      }
    }
  }, [
    contextKonteringsforslag,
    fakturaDelContext.activeKF,
    numberOfSavedKonteringsforslag,
  ]);

  const konteringsForslagWithoutAttest = useMemo(
    () =>
      fakturaDelContext.konteringsforslag.filter(konteringsforslag => {
        const kfAttestantId =
          konteringsforslag._Associations?.Fakturaattest?.id;
        if (kfAttestantId) {
          const foundBA =
            fakturaDelContext.beslutsAttestanterAllaFakturadelar.find(
              beslutAttestant =>
                beslutAttestant.id === kfAttestantId &&
                beslutAttestant.STATUS !==
                  TEDLRFakturaAttestSTATUSEnum.ATTESTERAD
            );
          return Boolean(foundBA);
        } else {
          return true;
        }
      }),
    [
      fakturaDelContext.konteringsforslag,
      fakturaDelContext.beslutsAttestanterAllaFakturadelar,
    ]
  );

  const fakturabelopp = useMemo(
    () => roundToTwoDecimals(fakturaDelContext.fakturaBelopp),
    [fakturaDelContext.fakturaBelopp]
  );
  const konteratBelopp = useMemo(
    () => getKonteratBelopp(fakturaDelContext.konteringsforslag),
    [fakturaDelContext.konteringsforslag]
  );

  const diff = useMemo(
    () => fakturabelopp - konteratBelopp,
    [fakturabelopp, konteratBelopp]
  );

  const unAssignedKFs = useMemo(
    () =>
      fakturaDelContext.konteringsforslag.filter(
        kf =>
          kf._Associations?.Fakturaattest?.id == null &&
          !fakturaDelContext.deletedKFs.some(
            deleted => deleted === kf.id && !isAutomaticallyAdded(kf)
          )
      ),
    [fakturaDelContext.deletedKFs, fakturaDelContext.konteringsforslag]
  );

  const unattestedBeslutsattestanter = useMemo(
    () =>
      fakturaDelContext.beslutsAttestanterAllaFakturadelar.filter(
        ba => ba.STATUS !== TEDLRFakturaAttestSTATUSEnum.ATTESTERAD
      ),
    [fakturaDelContext.beslutsAttestanterAllaFakturadelar]
  );

  const handleOpenAllKF = useCallback(() => {
    const arrayOfKF: string[] = konteringsForslagWithoutAttest.map(kf => kf.id);
    setActiveKF(arrayOfKF[0]);
    setExpandedKF(arrayOfKF);
  }, [setActiveKF, konteringsForslagWithoutAttest]);

  //Skapa kontering
  if (
    !(
      fakturaDelContext.fakturaBelopp == null ||
      isNaN(fakturaDelContext.fakturaBelopp) ||
      !fakturaDelContext.jagAttesterar
    ) &&
    diff !== 0 &&
    !formContext?.mutationLoading &&
    !formContext?.formState.loading &&
    formContext?.formState?.currentValues != null
  ) {
    // Hantera tillfällen då konteringsförslagen inte täcker upp hela fakturabeloppet, eller har för hög totalsumma
    // genom att skapa nytt konteringsförslag
    const addedCount = fakturaDelContext.konteringsforslag?.filter((kf: any) =>
      isAutomaticallyAdded(kf)
    ).length;
    if (konteratBelopp !== fakturabelopp) {
      const existingKonteringSaknas = fakturaDelContext.konteringsforslag?.find(
        (kf: any) =>
          konteringSaknas(kf._Associations) && kf.id !== newBeloppChangedId
      );

      const momsHantering: TEDLRLeverantorfakturaMOMSHANTERINGEnum | null =
        formContext?.formState.currentValues?._Associations?.Faktura
          ?.MOMSHANTERING;
      const registreratMomsBelopp =
        formContext?.formState.currentValues?._Associations?.Faktura
          ?.MomsBelopp2;

      const radtyp = getKonteringRadtyp(
        momsHantering,
        registreratMomsBelopp,
        false
      );

      let newValue = roundToTwoDecimals(diff);
      if (existingKonteringSaknas) {
        const rubrikbelopp = getRubrikbelopp(radtyp, existingKonteringSaknas);
        if (rubrikbelopp) {
          newValue += rubrikbelopp;
        }
      }
      newValue = roundToTwoDecimals(newValue);
      // Det existerande konteringsförslaget skulle bli 0, så vi tar bort det istället
      if (newValue === 0 && existingKonteringSaknas) {
        fakturaDelContext.deleteKonteringsforslag(
          newBeloppChangedId,
          existingKonteringSaknas.id,
          setNewBeloppChangedId,
          true
        );
      } else {
        const isAutoAdded =
          !existingKonteringSaknas ||
          (isAutomaticallyAdded(existingKonteringSaknas) &&
            !existingKonteringSaknas._Associations?.Kontodel1);

        const konteringsforslagId = existingKonteringSaknas
          ? existingKonteringSaknas.id
          : `${AUTOMATICALLY_ADDED_VALUE_PREFIX}${addedCount}_${shortid.generate()}`;

        const lastKF =
          fakturaDelContext.konteringsforslag?.[
            fakturaDelContext.konteringsforslag.length - 1
          ];

        const newSortNumber =
          existingKonteringSaknas?.SORTERINGS_NR != undefined
            ? existingKonteringSaknas?.SORTERINGS_NR
            : (lastKF?.SORTERINGS_NR ??
                fakturaDelContext.konteringsforslag.length) + 1;

        const utlandsMomsMallId =
          formContext?.formState.currentValues?._Associations?.Faktura
            ?._Associations?.Leverantor?._Associations?.UtlandskMoms?.id;
        const utlandskMomssats =
          formContext?.formState.currentValues?._Associations?.Faktura
            ?._Associations?.Leverantor?._Associations?.UtlandskMoms
            ?.UTL_MOMS_SATS;

        const defaultAvdragsratt = radtyp === 6 ? 1 : 0;

        const utlandskAvdrag =
          radtyp === 6
            ? getUtlandskAvdrag(newValue, utlandskMomssats, defaultAvdragsratt)
            : null;

        const newKonteringsForslagValues = getNewKonteringsforslagValues(
          konteringsforslagId,
          newSortNumber,
          radtyp,
          isAutoAdded,
          fakturaDelContext.fakturaId,
          newValue,
          utlandskMomssats,
          utlandskAvdrag,
          defaultAvdragsratt
        );

        const associatedObject: FormUpdateAssociatonType[] = [];
        if ((radtyp === 5 || radtyp === 6) && utlandsMomsMallId) {
          associatedObject.push({
            associationName: 'UtlandsMomsMall',
            operation: 'Ref',
            id: utlandsMomsMallId,
            values: {
              id: utlandsMomsMallId,
            },
          });
        }

        fakturaDelContext.addOrUpdateKonteringsforslag(
          newKonteringsForslagValues,
          isAutoAdded,
          associatedObject,
          true
        );
      }
    }
  }

  //Sätt beslutattestant på alla konteringsförslag som är tomma
  if (
    fakturaDelContext.jagAttesterar &&
    unattestedBeslutsattestanter?.length === 1 &&
    unAssignedKFs?.length !== 0
  ) {
    unAssignedKFs.forEach(kf => {
      fakturaDelContext.setBeslutsattestant(
        kf,
        unattestedBeslutsattestanter[0],
        isAutomaticallyAdded(kf),
        true
      );
    });
  }

  return (
    <EDKeybind alt global keymap="-" onKeyDown={handleCloseKF}>
      <EDKeybind alt global keymap="+" onKeyDown={handleOpenAllKF}>
        {numberOfSavedKonteringsforslag > 25 ? (
          <EDBox
            sx={{
              minHeight: containerHeight,
              width: '100%',
            }}
          >
            <EDList<FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All>
              data={contextKonteringsforslag}
              inCard
              item={(
                index: number,
                konteringsforslag: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All
              ) => {
                const key = 'kontering' + konteringsforslag?.id + index;
                const isAutoAdded = isAutomaticallyAdded(konteringsforslag);
                const avdragsRatt = getKonteringAvdragsRatt(
                  formContext?.formState.currentValues?._Associations?.Faktura
                    ?.MOMSHANTERING,
                  konteringsforslag
                );
                return (
                  <KonteringForslagComp
                    id={konteringsforslag.id}
                    autoFocusBelopp={
                      (fakturaDelContext.activeKF === ''
                        ? true
                        : fakturaDelContext.activeKF === konteringsforslag.id
                        ? true
                        : false) && !isSmallOrMedium
                    }
                    avdragsRatt={avdragsRatt}
                    key={key}
                    expanded={
                      expandedKF?.includes(konteringsforslag?.id) ?? false
                    }
                    isFIActive={props.isFIActive}
                    isNew={isAutoAdded}
                    konteringsforslag={konteringsforslag}
                    konteringsForslagIndex={index}
                    minFakturaAttest={currentFakturaAttest}
                    setExpanded={toggleExpandIndex(konteringsforslag?.id)}
                    setNewBeloppChange={setNewBeloppChangedId}
                    virtual
                  />
                );
              }}
              totalCount={contextKonteringsforslag.length}
              virtual
              ref={virtuoso}
            >
              {fakturaDelContext.konteringsforslag.map(
                (
                  konteringsforslag: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All,
                  i: number
                ) => {
                  const avdragsRatt = getKonteringAvdragsRatt(
                    formContext?.formState.currentValues?._Associations?.Faktura
                      ?.MOMSHANTERING,
                    konteringsforslag
                  );
                  return (
                    <KonteringForslagComp
                      id={konteringsforslag.id}
                      autoFocusBelopp={
                        (fakturaDelContext.activeKF === ''
                          ? true
                          : fakturaDelContext.activeKF === konteringsforslag.id
                          ? true
                          : false) && !isSmallOrMedium
                      }
                      avdragsRatt={avdragsRatt}
                      key={'kontering' + konteringsforslag.id + i}
                      expanded={
                        expandedKF?.includes(konteringsforslag?.id) ?? false
                      }
                      isFIActive={props.isFIActive}
                      isNew={isAutomaticallyAdded(konteringsforslag)}
                      konteringsforslag={konteringsforslag}
                      konteringsForslagIndex={i}
                      minFakturaAttest={currentFakturaAttest}
                      setExpanded={toggleExpandIndex(konteringsforslag?.id)}
                      setNewBeloppChange={setNewBeloppChangedId}
                    />
                  );
                }
              )}
            </EDList>
          </EDBox>
        ) : (
          fakturaDelContext.konteringsforslag.map(
            (
              konteringsforslag: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All,
              i: number
            ) => {
              const avdragsRatt = getKonteringAvdragsRatt(
                formContext?.formState.currentValues?._Associations?.Faktura
                  ?.MOMSHANTERING,
                konteringsforslag
              );

              return (
                <KonteringForslagComp
                  id={konteringsforslag.id}
                  autoFocusBelopp={
                    (fakturaDelContext.activeKF === ''
                      ? true
                      : fakturaDelContext.activeKF === konteringsforslag.id
                      ? true
                      : false) && !isSmallOrMedium
                  }
                  avdragsRatt={avdragsRatt}
                  key={'kontering' + konteringsforslag.id + i}
                  expanded={
                    expandedKF?.includes(konteringsforslag?.id) ?? false
                  }
                  isFIActive={props.isFIActive}
                  isNew={isAutomaticallyAdded(konteringsforslag)}
                  konteringsforslag={konteringsforslag}
                  konteringsForslagIndex={i}
                  minFakturaAttest={currentFakturaAttest}
                  setExpanded={toggleExpandIndex(konteringsforslag?.id)}
                  setNewBeloppChange={setNewBeloppChangedId}
                />
              );
            }
          )
        )}
      </EDKeybind>
    </EDKeybind>
  );
};

export default KonteringViewComp;
